/**
 * CONTACT-MGMT (CM-7) — standalone, wizard-agnostic ContactPicker.
 *
 * A reusable party-field control: search the **active** address book
 * (all-text typeahead) and, if the company isn't there yet, **add a new
 * contact inline** (reusing the CM-6 `ContactForm`) — returning a selected
 * `contactId`. Building it isolated is the point: CM-9 drops it into the
 * project/contract wizard as a thin step without this file touching any
 * wizard code (collision guard, CM-D13).
 *
 * Decisions:
 *   - CM-D10 — typeahead offers **active contacts only** (`includeDeleted`
 *     omitted/false); soft-deleted contacts never appear as fresh options.
 *     An already-linked contact (including a soft-deleted one) still
 *     **resolves its name by id** via `useContactQuery`, so historical links
 *     keep showing their label.
 *   - CM-D8 — "Add new" find-or-creates (the reused form runs the dedupe
 *     warn) and **selects** the new contact in one step.
 *   - CM-D2 — the reused `ContactForm` enforces the required postal block.
 *
 * Isolation (CM-7 AC-4): imports ONLY from `features/admin/contacts`
 * (form + hooks + types) and shared `@aeriosiq/ui` / MUI. It must NOT
 * import anything from `features/projects` or `features/contracts`.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ContactForm } from "../admin/contacts/ContactForm.js";
import {
  useContactQuery,
  useContactsListQuery,
} from "../admin/contacts/useContacts.js";
import type { ContactDetail } from "../admin/contacts/types.js";

export interface ContactPickerProps {
  /** Currently-selected contact id, or null. */
  value: string | null;
  /** Fired with the new selection's id (or null when cleared). */
  onChange: (contactId: string | null) => void;
  label?: string;
  disabled?: boolean;
  /** Field-level error text rendered under the control. */
  error?: string | null;
}

/** Lean option shape for the dropdown — company name + city to disambiguate. */
interface ContactOption {
  contactId: string;
  companyName: string | null;
  city: string | null;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

function detailToOption(d: ContactDetail): ContactOption {
  return { contactId: d.contactId, companyName: d.companyName, city: d.city };
}

export function ContactPicker({
  value,
  onChange,
  label = "Contact",
  disabled = false,
  error = null,
}: ContactPickerProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  // Remembers the option the user just picked / created so its label shows
  // immediately without a detail round-trip.
  const [localSelected, setLocalSelected] = useState<ContactOption | null>(
    null,
  );

  const debouncedInput = useDebounced(inputValue, 400);

  // Typeahead over the ACTIVE address book (CM-D10 — includeDeleted omitted).
  // Only fetches while the dropdown is open.
  const trimmed = debouncedInput.trim();
  const listQuery = useContactsListQuery(
    {
      ...(trimmed.length > 0 ? { q: trimmed } : {}),
      includeDeleted: false,
      page: 1,
      pageSize: 10,
    },
    { enabled: open },
  );

  const results = useMemo<ContactOption[]>(
    () =>
      (listQuery.data?.items ?? []).map((i) => ({
        contactId: i.contactId,
        companyName: i.companyName,
        city: i.city,
      })),
    [listQuery.data],
  );

  const localMatch = localSelected?.contactId === value ? localSelected : null;
  const resultMatch = results.find((r) => r.contactId === value) ?? null;

  // Resolve an externally-supplied (or soft-deleted) value by id when it is
  // not already in the local pick or the current search results (CM-D10).
  const needsResolve = value !== null && !localMatch && !resultMatch;
  const valueQuery = useContactQuery(needsResolve ? value : null);

  const selectedOption: ContactOption | null =
    value === null
      ? null
      : (localMatch ??
        resultMatch ??
        (valueQuery.data ? detailToOption(valueQuery.data) : null));

  // When the dropdown is closed, mirror the selected option's label into the
  // (controlled) input so an externally-supplied or freshly-resolved value
  // shows its company name. While open, the input is the live search query.
  useEffect(() => {
    if (!open) {
      setInputValue(selectedOption ? (selectedOption.companyName ?? "") : "");
    }
  }, [open, selectedOption]);

  // The selected option must be present in `options` or MUI warns; prepend it.
  const options = useMemo<ContactOption[]>(() => {
    if (
      selectedOption &&
      !results.some((r) => r.contactId === selectedOption.contactId)
    ) {
      return [selectedOption, ...results];
    }
    return results;
  }, [results, selectedOption]);

  function closeAdd(): void {
    setAddOpen(false);
  }

  function handleSaved(contact: ContactDetail): void {
    const option = detailToOption(contact);
    setLocalSelected(option);
    setInputValue(contact.companyName ?? "");
    onChange(contact.contactId);
    setAddOpen(false);
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Autocomplete<ContactOption>
          sx={{ flexGrow: 1 }}
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          disabled={disabled}
          options={options}
          value={selectedOption}
          inputValue={inputValue}
          onInputChange={(_e, next) => {
            setInputValue(next);
          }}
          loading={listQuery.isFetching}
          // BE already returns ranked, scoped matches — don't re-filter locally.
          filterOptions={(opts) => opts}
          isOptionEqualToValue={(a, b) => a.contactId === b.contactId}
          getOptionLabel={(o) => o.companyName ?? "—"}
          noOptionsText={
            trimmed.length > 0 ? "No active contacts match" : "Type to search"
          }
          onChange={(_e, next) => {
            setLocalSelected(next);
            onChange(next?.contactId ?? null);
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.contactId}>
              <Stack spacing={0}>
                <Typography variant="body2">
                  {option.companyName ?? "—"}
                </Typography>
                {option.city ? (
                  <Typography variant="caption" color="text.secondary">
                    {option.city}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          )}
          renderInput={(params) => {
            /* eslint-disable @typescript-eslint/no-explicit-any */
            const passThrough: any = params;
            return (
              <TextField
                {...passThrough}
                label={label}
                error={error !== null}
                helperText={error ?? undefined}
                size="small"
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    "aria-label": label,
                  },
                }}
                fullWidth
              />
            );
            /* eslint-enable */
          }}
        />
        <Button
          variant="outlined"
          size="medium"
          disabled={disabled}
          onClick={() => {
            setAddOpen(true);
          }}
          sx={{ flexShrink: 0, mt: 0 }}
        >
          Add new
        </Button>
      </Stack>

      <Dialog open={addOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
        <DialogTitle>Add contact</DialogTitle>
        <DialogContent>
          <ContactForm
            mode="create"
            onCancel={closeAdd}
            onSaved={handleSaved}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
